import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  useEffect,
} from "react";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { type Cycle } from "@src/packages/cycles/hooks/useCycles";
import { useOocyteCohorts } from "@src/packages/care/cryovault/useSpecimens";
import { buildTreatmentNavigationStack } from "./getNextTreatmentScreen";
import { parseTreatmentNotes } from "./transformFertilityTreatmentsState";
import type { FETHistoricalRecordInput } from "./useFETHistoricalRecords";
import {
  pickerTypeForCycleType,
  treatmentTypeForCycleType,
} from "./treatmentTypes";

export type FETProtocolClass = NonNullable<
  FETHistoricalRecordInput["protocolClass"]
>;
export type FETTransferState = NonNullable<
  FETHistoricalRecordInput["transferState"]
>;
export type FETThawState = NonNullable<FETHistoricalRecordInput["thawState"]>;
export type FETResultState = NonNullable<
  FETHistoricalRecordInput["resultState"]
>;

type CyclePhase = NonNullable<Cycle["phases"]>[number];
type FertilityMetric = NonNullable<CyclePhase["fertilityMetrics"]>[number];

export type FertilityTreatmentsState = {
  hasTreatmentsDone?: boolean | null;
  selectedTypes?: string[]; // e.g., iui, ivf, meds_only, other
  // Single source of truth: treatments array
  treatments: {
    id?: string;
    /** Server record ID for a typed FET historical entry, when available. */
    historyRecordId?: string;
    /** Stable idempotency key for the typed FET historical write. */
    historyClientRequestId?: string;
    type?: string | null; // api type
    name?: string | null;
    date?: string | null;
    clinic?: string | null;
    frozenEggCount?: number | null;
    notes?: string | null;
    /** Exact reported outcome, kept separate from patient-authored notes. */
    outcome?: string | null;
    /** Patient-reported facts remain explicit and unverified. */
    protocolClass?: FETProtocolClass;
    transferState?: FETTransferState;
    thawState?: FETThawState;
    resultState?: FETResultState;
    embryoCount?: number | null;
    provider?: Cycle["provider"];
    embryosFrozen?: FertilityMetric["value"];
    embryoFrozenPhaseId?: CyclePhase["id"];
    embryoFrozenMetricId?: FertilityMetric["id"];
    /**
     * Server ids for the egg-retrieval phase and its `retrieved_oocytes`
     * metric that carry the frozen-egg count when the treatment is an
     * `egg_freezing` cycle. Kept so a later edit patches the same records
     * rather than posting a duplicate.
     */
    eggRetrievalPhaseId?: CyclePhase["id"];
    eggRetrievedMetricId?: FertilityMetric["id"];
  }[];
  treatmentIdsToDelete: string[];
  // Dynamic navigation
  navigationStack?: string[];
  currentNavigationIndex?: number;
};

const initialState: FertilityTreatmentsState = {
  hasTreatmentsDone: null,
  selectedTypes: [],
  treatments: [],
  treatmentIdsToDelete: [],
  navigationStack: [],
  currentNavigationIndex: 0,
};

type Ctx = {
  state: FertilityTreatmentsState;
  updateState: (updates: Partial<FertilityTreatmentsState>) => void;
  resetState: () => void;
};

const FertilityTreatmentsContext = createContext<Ctx | undefined>(undefined);

type PersistedTreatmentCycle = Cycle;

type PatientWithPriorTreatmentAnswer = {
  fertilityHistory?: {
    hasPriorFertilityTreatments?: boolean | null;
  } | null;
};

function legacyFrozenEggCount(cycle: PersistedTreatmentCycle): number | null {
  const notes = cycle.dayNotes
    ?.map((dayNote) => dayNote.notes ?? "")
    .join("; ");
  const match = /(?:eggs_frozen|frozen_eggs)\s*:\s*(\d+)/i.exec(notes ?? "");
  return match ? Number(match[1]) : null;
}

function transformCycleToTreatment(
  cycle: PersistedTreatmentCycle,
  frozenEggCount?: number | null,
): {
  id?: string;
  type?: string | null;
  name?: string | null;
  date?: string | null;
  clinic?: string | null;
  frozenEggCount?: number | null;
  notes?: string | null;
  outcome?: string | null;
  provider?: Cycle["provider"];
  embryosFrozen?: FertilityMetric["value"];
  embryoFrozenPhaseId?: CyclePhase["id"];
  embryoFrozenMetricId?: FertilityMetric["id"];
  eggRetrievalPhaseId?: CyclePhase["id"];
  eggRetrievedMetricId?: FertilityMetric["id"];
} {
  const persistedType = cycle.rawCycleType || cycle.cycleType;
  const frontendType = persistedType
    ? treatmentTypeForCycleType(persistedType)
    : null;

  const dayNotesText = (cycle.dayNotes ?? [])
    .map((dn) => dn.notes)
    .filter(Boolean)
    .join("; ");
  const legacyNotes = parseTreatmentNotes(dayNotesText);
  const outcome = cycle.outcome
    ? (cycle.outcome.pregnancyOutcome ??
      cycle.outcome.miscarriageOutcome ??
      cycle.outcome.type ??
      null)
    : legacyNotes.outcome;

  const frozenMetricEntry = (cycle.phases ?? [])
    .flatMap((phase) =>
      (phase.fertilityMetrics ?? []).map((metric) => ({ phase, metric })),
    )
    .find(({ metric }) => metric.type === "frozen_embryo");
  const retrievedMetricEntry = (cycle.phases ?? [])
    .flatMap((phase) =>
      (phase.fertilityMetrics ?? []).map((metric) => ({ phase, metric })),
    )
    .find(({ metric }) => metric.type === "retrieved_oocytes");
  const legacyFrozenCount = Number.parseInt(
    legacyNotes.embryosFrozen ?? "",
    10,
  );

  return {
    id: cycle.id,
    type: frontendType,
    name: cycle.description || null,
    date: cycle.startDate || null,
    clinic: cycle.clinic ?? cycle.provider ?? legacyNotes.clinic ?? null,
    frozenEggCount:
      frozenEggCount ??
      retrievedMetricEntry?.metric.value ??
      cycle.oocyteSpecimens?.length ??
      legacyFrozenEggCount(cycle),
    // Keep legacy note text intact on read. New saves use dayNotes as the
    // structured note field instead of embedding metadata in semicolon text.
    notes: cycle.outcome?.notes ?? dayNotesText ?? null,
    outcome,
    provider: cycle.provider ?? legacyNotes.clinic ?? null,
    embryosFrozen:
      frozenMetricEntry?.metric.value ??
      (Number.isInteger(legacyFrozenCount) ? legacyFrozenCount : null),
    embryoFrozenPhaseId: frozenMetricEntry?.phase.id,
    embryoFrozenMetricId: frozenMetricEntry?.metric.id,
    eggRetrievalPhaseId: retrievedMetricEntry?.phase.id,
    eggRetrievedMetricId: retrievedMetricEntry?.metric.id,
  };
}

export function FertilityTreatmentsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { data: patient, isLoading } = usePatient();
  const { data: oocyteCohorts, isLoading: isLoadingOocyteCohorts } =
    useOocyteCohorts();
  const [state, setState] = useState<FertilityTreatmentsState>(initialState);

  useEffect(() => {
    if (isLoading || isLoadingOocyteCohorts || !patient) return;

    const cycles =
      (patient as typeof patient & { cycles?: PersistedTreatmentCycle[] })
        .cycles || [];
    const pastCycles = cycles.filter(
      (c) => c.status === "closed" && (c.cycleType || c.rawCycleType),
    );
    const hasPriorFertilityTreatments = (
      patient as typeof patient & PatientWithPriorTreatmentAnswer
    ).fertilityHistory?.hasPriorFertilityTreatments;

    setState((prev) => {
      const isInitialState =
        prev.hasTreatmentsDone === null && prev.treatments.length === 0;
      const next: Partial<FertilityTreatmentsState> = {};

      if (isInitialState || prev.hasTreatmentsDone === null) {
        if (pastCycles.length > 0) {
          next.hasTreatmentsDone = true;
        } else if (hasPriorFertilityTreatments !== undefined) {
          next.hasTreatmentsDone = hasPriorFertilityTreatments;
        }
      }

      if (isInitialState && pastCycles.length > 0) {
        next.treatments = pastCycles.map((cycle) =>
          transformCycleToTreatment(
            cycle,
            oocyteCohorts?.find((cohort) => cohort.originCycleId === cycle.id)
              ?.numberFrozen,
          ),
        );

        const types = Array.from(
          new Set(
            pastCycles
              .map((c) => {
                const persistedType = c.rawCycleType || c.cycleType;
                return persistedType
                  ? pickerTypeForCycleType(persistedType)
                  : null;
              })
              .filter((t: unknown): t is string => Boolean(t)),
          ),
        ) as string[];
        next.selectedTypes = types;
        next.navigationStack = buildTreatmentNavigationStack(types);
        next.currentNavigationIndex = 0;
      }

      return Object.keys(next).length === 0 ? prev : { ...prev, ...next };
    });
  }, [patient, isLoading, isLoadingOocyteCohorts, oocyteCohorts]);

  const updateState = useCallback(
    (updates: Partial<FertilityTreatmentsState>) => {
      setState((prev) => ({ ...prev, ...updates }));
    },
    [],
  );

  const resetState = useCallback(() => setState(initialState), []);

  return (
    <FertilityTreatmentsContext.Provider
      value={{ state, updateState, resetState }}
    >
      {children}
    </FertilityTreatmentsContext.Provider>
  );
}

export function useFertilityTreatments() {
  const ctx = useContext(FertilityTreatmentsContext);
  if (!ctx) {
    throw new Error(
      "useFertilityTreatments must be used within FertilityTreatmentsProvider",
    );
  }
  return ctx;
}
